import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import * as routes from "@/constants/routes";
import { strings } from "@/I18n";
import type { RootState } from "@/types/redux";
import { isSuperAdmin, isSuperAdminOrCityAdmin } from "@/utils/utils";
import NotFound from "@/v2/containers/NotFoundContainer";
import { useEffectiveWhitelabelId } from "@/v2/hooks/useEffectiveWhitelabelId";
import type { FeatureFlags } from "@/v2/hooks/useFeatureFlags";
import { shouldOfferVehicleModels } from "@/v2/hooks/useVehicleModelsEnabled";
import {
  AnalyticsContainer,
  ApiDocsContainer,
  AppConfigurationContainer,
  AppVersionsContainer,
  BikeContainer,
  BikeHistoryContainer,
  ChatAnalyticsContainer,
  CityContainer,
  ClientContainer,
  CouponsScreen,
  CouponUserContainer,
  CustomerRestrictionsContainer,
  DockContainer,
  FeatureFlagsContainer,
  FineDetailContainer,
  FinesContainer,
  IntrosContainer,
  LocksContainer,
  MapContainerV2,
  MFAContainer,
  NotificationsContainer,
  OrganizationContainer,
  OverviewContainer,
  PartnerDetailContainer,
  PartnersContainer,
  PricingHub,
  PricingPlanDomains,
  PricingPlanEditor,
  ReferralsScreen,
  RentalRulesContainer,
  RentalsContainer,
  RentalsGallery,
  ReservationsContainer,
  RewardsContainer,
  ServiceAccountsContainer,
  ServiceHealthContainer,
  SettingsContainer,
  StationContainer,
  SupersetDashboardContainer,
  TopUpBonusesContainer,
  TransactionContainer,
  UserLedger,
  UserManagementScreen,
  VehicleModelDetailContainer,
  VehicleModelsContainer,
  WarningsContainer,
} from "../constants";
import type { FilterProps, OrderProps, PageConfig } from "../types";

/**
 * Old ride-passes links land on the hub's ride-passes tab. The dashboard is
 * mounted under both /dashboard and /v2/dashboard, so the prefix is read off
 * the live path rather than assumed.
 */
function RidePassesRedirect() {
  const { pathname, search } = useLocation();
  const base = pathname.startsWith("/v2") ? "/v2/dashboard" : "/dashboard";
  const params = new URLSearchParams(search);
  params.set("tab", "ride-passes");
  return (
    <Navigate to={`${base}/pricing?${params.toString()}`} replace={true} />
  );
}

interface DashboardRoutesProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  clearSearchValue: () => void;
  setCurrentPageValue: (page: number) => void;
  currentPageValue: number;
  orderProps: OrderProps;
  orderValue: string;
  filterProps: FilterProps;
  adminDetails: RootState["auth"]["adminDetails"];
  featureFlags: FeatureFlags;
}

export function DashboardRoutes({
  searchValue,
  setSearchValue,
  clearSearchValue,
  setCurrentPageValue,
  currentPageValue,
  orderProps,
  orderValue,
  filterProps,
  adminDetails,
  featureFlags,
}: DashboardRoutesProps) {
  const changeSearch = setSearchValue;

  // Kept beside the sidebar's own gate by sharing one predicate: a route that
  // disagrees with the entry either 404s a visible link or leaves a reachable
  // URL with no way in.
  const whitelabelId = useEffectiveWhitelabelId();
  const vehicleModelsOffered = shouldOfferVehicleModels(
    featureFlags,
    adminDetails,
    whitelabelId
  );

  const pages: PageConfig[] = [
    {
      path: routes.MFA_ROUTE,
      Component: MFAContainer,
    },
    {
      path: routes.DASHBOARD_USERS,
      Component: ClientContainer,
    },
    {
      path: routes.DASHBOARD_BIKES,
      extraProps: {
        changeSearch: changeSearch,
        searchValue: searchValue,
        clearSearchValue: clearSearchValue,
        changePage: setCurrentPageValue,
        currentPageValue: currentPageValue,
        orderProps: orderProps,
        orderValue: orderValue,
        filterProps: filterProps,
      },
      Component: BikeContainer,
    },
    {
      path: routes.DASHBOARD_LOCKS,
      Component: LocksContainer,
    },
    {
      path: routes.DASHBOARD_STATIONS,
      Component: StationContainer,
    },
    {
      path: routes.DASHBOARD_DOCKS,
      extraProps: {
        changeSearch: changeSearch,
        searchValue: searchValue,
        clearSearchValue: clearSearchValue,
        changePage: setCurrentPageValue,
        currentPageValue: currentPageValue,
        orderProps: orderProps,
        filterProps: filterProps,
      },
      Component: DockContainer,
    },
    {
      path: routes.DASHBOARD_RENTALS,
      extraProps: {
        changeSearch: changeSearch,
        searchValue: searchValue,
        clearSearchValue: clearSearchValue,
        changePage: setCurrentPageValue,
        currentPageValue: currentPageValue,
        orderProps: orderProps,
        orderValue: orderValue,
        filterProps: filterProps,
      },
      Component: RentalsContainer,
    },
    {
      path: routes.DASHBOARD_RENTALS_GALLERY,
      Component: RentalsGallery,
    },
    ...(isSuperAdminOrCityAdmin(adminDetails)
      ? [
          {
            path: routes.DASHBOARD_RENTAL_RULES,
            Component: RentalRulesContainer,
          },
          { path: routes.SETTINGS_ROUTE, Component: SettingsContainer },
          {
            path: routes.CUSTOMER_RESTRICTIONS_ROUTE,
            Component: CustomerRestrictionsContainer,
          },
          {
            // Legacy: Metabase analytics. Scheduled for removal once Superset (DASHBOARD_ANALYTICS_V2) is fully rolled out.
            path: routes.DASHBOARD_ANALYTICS,
            Component: AnalyticsContainer,
          },
        ]
      : []),
    {
      path: routes.APP_CONFIGURATION_ROUTE,
      Component: AppConfigurationContainer,
    },
    ...(isSuperAdmin(adminDetails)
      ? [
          {
            path: routes.USER_MANAGEMENT_ROUTE,
            Component: UserManagementScreen,
          },
          {
            path: routes.SERVICE_ACCOUNTS_ROUTE,
            Component: ServiceAccountsContainer,
          },
        ]
      : []),
    {
      path: routes.DASHBOARD_TRANSACTIONS,
      Component: TransactionContainer,
    },
    {
      path: routes.BIKE_HISTORY,
      Component: BikeHistoryContainer,
    },
    {
      path: routes.USER_LEDGER,
      Component: UserLedger,
    },
    {
      path: routes.DASHBOARD_CUSTOMER,
      Component: UserLedger,
      requiresNewBackend: true,
    },
    {
      path: routes.COUPONS_ROUTE,
      Component: CouponsScreen,
    },
    {
      path: routes.PROMOTIONS_REWARDS_ROUTE + "/*",
      Component: RewardsContainer,
    },
    {
      path: routes.PROMOTIONS_TOPUP_BONUSES_ROUTE,
      Component: TopUpBonusesContainer,
    },
    {
      path: routes.PRICING_HUB_ROUTE,
      Component: PricingHub,
    },
    {
      // The old ride-passes entry point. Ride passes now live in the hub, and a
      // bookmark from before the move must land on the tab it used to open
      // rather than on a second copy of the same screen.
      path: routes.PROMOTIONS_RIDE_PASSES_ROUTE,
      Component: RidePassesRedirect,
    },
    {
      path: routes.REFERRAL_ROUTE,
      Component: ReferralsScreen,
    },
    {
      path: routes.PRICING_PLAN_EDITOR_ROUTE,
      Component: PricingPlanEditor,
    },
    {
      path: routes.PRICING_PLAN_EDITOR_CREATE_ROUTE,
      Component: PricingPlanEditor,
    },
    {
      path: routes.PRICING_PLAN_DOMAINS_ROUTE,
      Component: PricingPlanDomains,
    },
    {
      path: routes.USER_COUPON_ROUTE,
      Component: CouponUserContainer,
    },
    {
      path: routes.INTROS_ROUTE,
      Component: IntrosContainer,
    },
    {
      path: routes.WARNINGS_ROUTE,
      Component: WarningsContainer,
    },
    {
      path: routes.DASHBOARD_RESERVATIONS,
      Component: ReservationsContainer,
      requiresNewBackend: true,
    },
    {
      path: routes.DASHBOARD_USER,
      Component: ClientContainer,
    },
    {
      path: routes.DASHBOARD_BIKE,
      extraProps: {
        changeSearch: changeSearch,
        searchValue: searchValue,
        clearSearchValue: clearSearchValue,
        changePage: setCurrentPageValue,
        orderProps: orderProps,
        filterProps: filterProps,
      },
      Component: BikeContainer,
    },
    {
      path: routes.DASHBOARD_TRANSACTION,
      Component: TransactionContainer,
    },
    {
      // The map now lives on its own tab; the index is the operations overview.
      path: routes.DASHBOARD_MAP,
      Component: MapContainerV2,
    },
    {
      path: "",
      Component: OverviewContainer,
    },
  ];

  return (
    <Suspense
      fallback={
        <div className="tw-flex tw-items-center tw-justify-center tw-h-full tw-min-h-[200px]">
          <div className="tw-animate-pulse tw-text-muted-foreground tw-text-sm">
            {strings("common.loading")}
          </div>
        </div>
      }
    >
      <Routes>
        {pages
          .filter(
            ({ requiresNewBackend }) =>
              !requiresNewBackend ||
              isSuperAdmin(adminDetails) ||
              adminDetails?.newBackendEnabled
          )
          .map(({ path, Component, extraProps }) => (
            <Route
              path={path.replace(routes.DASHBOARD_SCREEN, "")}
              key={path}
              element={<Component {...extraProps} />}
            />
          ))}
        {adminDetails?.cityId === null ? (
          <Route
            path={routes.DASHBOARD_CITIES.replace(routes.DASHBOARD_SCREEN, "")}
            element={<CityContainer />}
          />
        ) : null}
        {(isSuperAdmin(adminDetails) ||
          (isSuperAdminOrCityAdmin(adminDetails) &&
            adminDetails?.newBackendEnabled)) && (
          <Route
            path={routes.NOTIFICATIONS_ROUTE.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<NotificationsContainer />}
          />
        )}
        {isSuperAdmin(adminDetails) && (
          <Route
            path={routes.APP_VERSIONS_ROUTE.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<AppVersionsContainer />}
          />
        )}
        {isSuperAdmin(adminDetails) && (
          <Route
            path={routes.SERVICE_HEALTH_ROUTE.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<ServiceHealthContainer />}
          />
        )}
        {isSuperAdmin(adminDetails) && (
          <Route
            path={routes.API_DOCS_ROUTE.replace(routes.DASHBOARD_SCREEN, "")}
            element={<ApiDocsContainer />}
          />
        )}
        {isSuperAdmin(adminDetails) && (
          <Route
            path={routes.FEATURE_FLAGS_ROUTE.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<FeatureFlagsContainer />}
          />
        )}
        {isSuperAdmin(adminDetails) && (
          <Route
            path={routes.DASHBOARD_ORGANIZATIONS.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<OrganizationContainer />}
          />
        )}
        {featureFlags.supersetAnalytics && (
          <Route
            path={routes.DASHBOARD_ANALYTICS_V2.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<SupersetDashboardContainer />}
          />
        )}
        {isSuperAdminOrCityAdmin(adminDetails) && featureFlags.aiAnalytics && (
          <Route
            path={routes.DASHBOARD_CHAT_ANALYTICS.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<ChatAnalyticsContainer />}
          />
        )}
        {isSuperAdminOrCityAdmin(adminDetails) &&
          featureFlags.partnerAccess && (
            <Route
              path={routes.DASHBOARD_PARTNERS.replace(
                routes.DASHBOARD_SCREEN,
                ""
              )}
              element={<PartnersContainer />}
            />
          )}
        {isSuperAdminOrCityAdmin(adminDetails) &&
          featureFlags.partnerAccess && (
            <Route
              path={routes.DASHBOARD_PARTNER_DETAIL.replace(
                routes.DASHBOARD_SCREEN,
                ""
              )}
              element={<PartnerDetailContainer />}
            />
          )}
        {/* City admins are refused outright: a vehicle model spans the whole
            organization, so the backend answers 403 on every one of these
            endpoints. Otherwise the flag decides, except where it cannot yet be
            read — see `shouldOfferVehicleModels`. The container renders the
            prompt in that case; the route has to exist for it to do so. */}
        {vehicleModelsOffered && (
          <Route
            path={routes.DASHBOARD_VEHICLE_MODELS.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<VehicleModelsContainer />}
          />
        )}
        {vehicleModelsOffered && (
          <Route
            path={routes.DASHBOARD_VEHICLE_MODEL_DETAIL.replace(
              routes.DASHBOARD_SCREEN,
              ""
            )}
            element={<VehicleModelDetailContainer />}
          />
        )}
        {/* Both routes are always registered. Whether an operator can actually
            use fines is decided by their payment configuration, which the
            containers ask about — a route that existed only for migrated
            operators would make the "not set up for fines" message
            unreachable. */}
        <Route
          path={routes.DASHBOARD_FINES.replace(routes.DASHBOARD_SCREEN, "")}
          element={<FinesContainer />}
        />
        <Route
          path={routes.DASHBOARD_FINE_DETAIL.replace(
            routes.DASHBOARD_SCREEN,
            ""
          )}
          element={<FineDetailContainer />}
        />
        <Route path={"/dashboard/*"} element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
